import XLSX from "xlsx";
//@ts-ignore
import _ from "lodash";
import Reading from "spectral-core/models/Reading";
import { cultureTypeLabelExport } from "spectral-core/cultureEnum";

let header = [];

let parameterHeader: string[] = [];
let wavelengthHeader: number[] = [];

enum MahalanobisStatus {
  NA = 0,
  Distant = 1,
  Near = 2,
  Compatible = 3
}

type NormalizedParameter = {
  ParameterName: string;
  Value: number | string;
  ReferenceValue?: string;
  MahalanobisValue?: number | string;
  MahalanobisStatus: MahalanobisStatus
};

// Campos ICL
const ICL_FIELDS_MAP = [
  { prop: "culture", header: "Culture", translator: cultureTypeLabelExport },
  { prop: "cultivate", header: "Cultivate" },
  { prop: "growthStage", header: "GrowthStage" },
  { prop: "plantThird", header: "PlantThird", translator: translatePlantThird },
  { prop: "plot", header: "Plot" },
  { prop: "customCultureName", header: "CustomCultureName" },
  { prop: "fieldIdentifier", header: "FieldIdentifier" },
  { prop: "sampleCode", header: "SampleCode" },
  { prop: "washed", header: "Washed", translator: translateBoolean },
  { prop: "collectTime", header: "CollectTime" },
  { prop: "depth", header: "Depth" },
  { prop: "sugarcaneType", header: "SugarcaneType", translator: translateSugarcaneType},
  { prop: "whitevalidation", header: "WhiteValidation"}
];

const getIclFieldsToInclude = (foundRow: Reading[]) => {
  return ICL_FIELDS_MAP.filter((field) => {
    // @ts-ignore
    return foundRow.some((reading) => reading[field.prop] != null);
  });
};

//@ts-ignore
export const fillHeader = (foundRow: Reading[], userAccessLevel?: number) => {
  header = [];

  header.push("ID");
  header.push("UserName");
  header.push("Site");
  header.push("SampleName");
  header.push("DataTime");
  header.push("SyncedAt");
  header.push("Type");
  header.push("TypeDescription");
  header.push("Comment");
  header.push("S/N");
  header.push("Temperature");
  header.push("Coordinates");

  if (userAccessLevel !== 5) {
    buildWavelengthHeader(foundRow);
  }

  let allParameters = foundRow.map((f) => 
    normalizeParameters(JSON.parse(f.predictedValues))).flat();

  parameterHeader = addAllParametersToHeader(allParameters);

  const iclFieldsToInclude = getIclFieldsToInclude(foundRow);
  iclFieldsToInclude.forEach((field) => {
    header.push(field.header);
  });

  return header;
};

export function fillDataInExcel(
  foundRow: Reading[],
  userAccessLevel?: number
) {
  const iclFieldsToInclude = getIclFieldsToInclude(foundRow);
  return foundRow.map((reading) =>
    DataTemplate(reading, parameterHeader, iclFieldsToInclude, userAccessLevel)
  );
}

function DataTemplate(reading: Reading, parametersHeader: string[], iclFieldsToInclude: typeof ICL_FIELDS_MAP, userAccessLevel?: number) {
  let data = [];

  let syncedAt = new Date(reading.syncedToCloudAt)
    .toLocaleString()
    .replace(',', '');

  data.push(reading.id);
  data.push(reading.userName);
  data.push(reading.userSite);
  data.push(reading.name);
  data.push(reading.dateTime);
  data.push(syncedAt);
  data.push(reading.type);
  data.push(reading.typeDescription);
  data.push(reading.comment);
  data.push(reading.serialNumber);
  data.push(reading.temperature);
  data.push(reading.geoLocation);

  if (userAccessLevel !== 5 && reading.signal != null && typeof reading.signal === "string")
  {
    addSignal(
      JSON.parse(reading.signal),
      JSON.parse(reading.wavelenght),
      wavelengthHeader,
      data
    );
  }

  if (
    reading.predictedValues != null &&
    typeof reading.predictedValues === "string"
  ) {
    addParameters(normalizeParameters(JSON.parse(reading.predictedValues)), parametersHeader, data);
  }

  iclFieldsToInclude.forEach((field) => {
    // @ts-ignore
    const value = reading[field.prop];
    if (field.translator) {
      data.push(field.translator(value));
    } else {
      data.push(value ?? "");
    }
  });

  return data;
}

function translatePlantThird(plantThird?: number): string {
  switch (plantThird) {
    case 0: return "Upper";
    case 1: return "Middle";
    case 2: return "Lower";
    case 3: return "All";
    case 4: return "Upper Middle";
    default: return "";
  }
}

function translateSugarcaneType(sugarcaneType?: number): string {
  switch (sugarcaneType) {
    case 0: return "Stalk";
    case 1: return "Leaf";
    default: return "";
  }
}

function translateBoolean(washed?: boolean): string {
  switch (washed) {
    case false: return "No";
    case true: return "Yes";
    default: return "";
  }
}

const addParameters = (
  normalizedParams: NormalizedParameter[],
  parameterHeader: string[],
  data: any[]
) => {
  parameterHeader.forEach((parameterName) => {
    const found = normalizedParams.find(
      p => p.ParameterName === parameterName
    );

    if (found) {
      data.push(found.Value ?? 0);
      data.push(found.ReferenceValue ?? "");
      data.push(found.MahalanobisValue ?? "");
      data.push(
        found.MahalanobisStatus === MahalanobisStatus.NA 
        ? ""
        : MahalanobisStatus[found.MahalanobisStatus]);
    } else {
      data.push(0);
      data.push("");
      data.push("");
      data.push("");
    }
  });
};

const addSignal = (
  signal: number[],
  wavelengths: number[] | null,
  globalHeader: number[],
  arrayToBeFilled: any[]
) => {
  if (wavelengths === null)
  {
    globalHeader.forEach((g) => 
      arrayToBeFilled.push(""));
    return;
  }

  const map = new Map<number, number>();
  wavelengths.forEach((w, i) => {
    map.set(w, signal[i]);
  });

  globalHeader.forEach((w) => {
    const value = map.get(w);
    arrayToBeFilled.push(value ?? "");
  });
};

export const normalizeParameters = (input: any): NormalizedParameter[] => {
  if (Array.isArray(input)) {
    return normalizeRegression(input);
  }

  if (input && typeof input === "object") {
    return [
      ...normalizeRegression(input.Regression),
      ...normalizeClassification(input.Classification)
    ];
  }

  return [];
};

const normalizeRegression = (regression: any): NormalizedParameter[] => {
  if (!Array.isArray(regression)) return [];

  return regression.map(p => ({
    ParameterName: p.ParameterName,
    Value: p.Value ?? 0,
    ReferenceValue: p.ReferenceValue ?? "",
    MahalanobisValue: p.NormalizedValue ?? p.MahalanobisValue ?? "",
    MahalanobisStatus: p.MahalanobisStatus ?? p.MahalanobisStatusValue ?? MahalanobisStatus.NA
  }));
};

const normalizeClassification = (classifications: any): NormalizedParameter[] => {
  if (!Array.isArray(classifications) || !classifications.length) return [];

  const classification = classifications[0];

  const result: NormalizedParameter[] = [];

  result.push({
    ParameterName: "PredictedClass",
    Value: classification.PredictedClass,
    MahalanobisValue: classification.NormalizedValue ?? classification.MahalanobisValue ?? "",
    MahalanobisStatus: classification.MahalanobisStatus ?? MahalanobisStatus.NA
  });

  Object.entries(classification.ClassProbabilities || {}).forEach(
    ([key, value]) => {
      result.push({
        ParameterName: key,
        Value: value as number,
        MahalanobisStatus: MahalanobisStatus.NA
      });
    }
  );

  return result;
};

//@ts-ignore
const addWaveLenghtToHeader = (waveLenght) => {
  //@ts-ignore
  waveLenght.forEach((value) => {
    header.push(value.toString());
  });
};

const buildWavelengthHeader = (foundRow: Reading[]) => {
  const all = foundRow
    .filter(r => r.wavelenght)
    .map(r => JSON.parse(r.wavelenght))
    .flat()
    .filter(w => w != null);

  wavelengthHeader = Array.from(new Set(all)).sort((a, b) => a - b);

  wavelengthHeader.forEach(w => {
    header.push(w.toString());
  });
};

const addAllParametersToHeader = (params: NormalizedParameter[]) => {
  const uniqueParams = Array.from(
    new Set(params.map(p => p.ParameterName))
  );

  uniqueParams.forEach(name => {
    header.push(name);
    header.push(`Ref.Lab ${name}`);
    header.push(`Mahalanobis ${name}`)
    header.push(`Mahalanobis Status ${name}`)
  });

  return uniqueParams;
};

//@ts-ignore
export const createStatisticsSheet = (xlsxObject, statisticsData) => {
  for (let index = 0; index < statisticsData.length - 1; index++) {
    xlsxObject.SheetNames.push(statisticsData[index].type);

    let ws_data = [];

    ws_data.push(_.map(statisticsData[index].data, "x"));
    ws_data.push(_.map(statisticsData[index].data, "y"));

    var ws = XLSX.utils.aoa_to_sheet(ws_data);
    xlsxObject.Sheets[statisticsData[index].type] = ws;
  }
};
